import { useAuth } from '../context/AuthContext'
import { useGame } from '../context/GameContext'
import { useNavigate } from 'react-router-dom'
import { Button } from '../components/ui/Button'
import { Card, CardContent } from '../components/ui/Card'
import { useToast } from '../components/ui/Toaster'
import { 
  Plus, Users, Trophy, Clock, CheckCircle, 
  XCircle, AlertCircle, Share2, Copy, Check
} from 'lucide-react'
import { clsx } from 'clsx'
import { useState, useEffect } from 'react'
import { format } from 'date-fns'

export default function Duel() {
  const { user, profile } = useAuth()
  const { duels, loading: duelsLoading, fetchDuels, createDuel, dailyPrompt } = useGame()
  const { toast } = useToast()
  const navigate = useNavigate()
  const [creating, setCreating] = useState(false)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    fetchDuels()
  }, [fetchDuels])

  const handleCreateDuel = async () => {
    if (!dailyPrompt || !user) return
    
    setCreating(true)
    try {
      const { inviteCode, error } = await createDuel(dailyPrompt.id)
      if (error) throw error
      if (inviteCode) {
        const duelUrl = `${window.location.origin}/duel/${inviteCode}`
        await navigator.clipboard.writeText(duelUrl)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
        toast({ type: 'success', title: 'Duel created!', message: 'Link copied to clipboard. Send it to a friend!' })
      }
    } catch (err) {
      toast({ type: 'error', title: 'Failed to create duel', message: err instanceof Error ? err.message : 'Try again' })
    } finally {
      setCreating(false)
    }
  }

  const copyInviteLink = async (inviteCode: string) => {
    const url = `${window.location.origin}/duel/${inviteCode}`
    await navigator.clipboard.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
    toast({ type: 'success', title: 'Link copied!' })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'waiting': return 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/30'
      case 'playing': return 'text-brand-600 dark:text-brand-400 bg-brand-50 dark:bg-brand-900/30'
      case 'completed': return 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30'
      default: return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'waiting': return <Clock className="w-4 h-4" />
      case 'playing': return <Users className="w-4 h-4" />
      case 'completed': return <CheckCircle className="w-4 h-4" />
      default: return <XCircle className="w-4 h-4" />
    }
  }

  const formatTimeRemaining = (expiresAt: string) => {
    const now = new Date()
    const expires = new Date(expiresAt)
    const diff = expires.getTime() - now.getTime()
    
    if (diff <= 0) return 'Expired'
    
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    
    if (hours > 0) return `${hours}h ${minutes}m`
    return `${minutes}m`
  }

  if (duelsLoading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-6 lg:py-10 space-y-6">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold text-surface-900 dark:text-surface-100">Duels</h1>
        </div>
        <Card className="text-center p-8">
          <div className="w-12 h-12 mx-auto mb-4 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-surface-600 dark:text-surface-400">Loading duels...</p>
        </Card>
      </div>
    )
  }

  const activeDuels = duels.filter(d => d.status === 'waiting' || d.status === 'playing')
  const completedDuels = duels.filter(d => d.status === 'completed')
  const expiredDuels = duels.filter(d => d.status === 'expired' || new Date(d.expires_at) < new Date())

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 lg:py-10 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-surface-900 dark:text-surface-100">Duels</h1>
          <p className="text-surface-500 dark:text-surface-400">Challenge friends and test your sync</p>
        </div>
        <Button 
          className="w-full sm:w-auto" 
          size="lg" 
          onClick={handleCreateDuel} 
          loading={creating}
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Duel
        </Button>
      </div>

      {/* Active Duels */}
      {(activeDuels.length > 0 || completedDuels.length > 0) && (
        <>
          {activeDuels.length > 0 && (
            <section className="space-y-4">
              <h2 className="text-lg font-semibold text-surface-900 dark:text-surface-100 flex items-center gap-2">
                <Clock className="w-5 h-5 text-yellow-500" />
                Active Duels
              </h2>
              <div className="space-y-3">
                {activeDuels.map(duel => (
                  <Card key={duel.id} className="ring-2 ring-yellow-500/30">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                            {duel.creator_username?.[0]?.toUpperCase() || 'C'}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-surface-900 dark:text-surface-100 truncate">
                                {duel.prompt?.spectrum_a_label || 'Daily Overlap'}
                              </h3>
                              <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium', getStatusColor(duel.status))}>
                                {getStatusIcon(duel.status)}
                                {duel.status.charAt(0).toUpperCase() + duel.status.slice(1)}
                              </span>
                            </div>
                            <p className="text-sm text-surface-500 dark:text-surface-400 truncate">
                              vs {duel.opponent_username || 'Waiting for opponent...'}
                            </p>
                            <p className="text-xs text-surface-400 mt-1">
                              Expires in {formatTimeRemaining(duel.expires_at)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {duel.status === 'playing' && (
                            <Button 
                              size="sm" 
                              onClick={() => navigate(`/duel/${duel.invite_code}/play`)}
                              className="whitespace-nowrap"
                            >
                              <Users className="w-4 h-4 mr-2" />
                              Play Now
                            </Button>
                          )}
                          {duel.status === 'waiting' && (
                            <Button 
                              variant="secondary" 
                              size="sm" 
                              onClick={() => copyInviteLink(duel.invite_code)}
                              className="whitespace-nowrap"
                            >
                              <Share2 className="w-4 h-4 mr-2" />
                              Share
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          )}

          {completedDuels.length > 0 && (
            <section className="space-y-4">
              <h2 className="text-lg font-semibold text-surface-900 dark:text-surface-100 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-green-500" />
                Completed Duels
              </h2>
              <div className="space-y-3">
                {completedDuels.slice(0, 5).map(duel => (
                  <Card key={duel.id}>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                            <CheckCircle className="w-6 h-6" />
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-semibold text-surface-900 dark:text-surface-100 truncate">
                              {duel.prompt?.spectrum_a_label || 'Daily Overlap'}
                            </h3>
                            <p className="text-sm text-surface-500 dark:text-surface-400 truncate">
                              vs {duel.opponent_username || 'Unknown'}
                            </p>
                            <p className="text-xs text-surface-400 mt-1">
                              Completed {format(new Date(duel.updated_at || duel.created_at), 'MMM d, yyyy')}
                            </p>
                          </div>
                        </div>
                        <Button 
                          variant="secondary" 
                          size="sm" 
                          onClick={() => navigate(`/duel/${duel.invite_code}/result`)}
                          className="whitespace-nowrap flex-shrink-0"
                        >
                          <Trophy className="w-4 h-4 mr-2" />
                          View Result
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {completedDuels.length > 5 && (
                  <p className="text-center text-surface-500 text-sm">
                    +{completedDuels.length - 5} more completed duels
                  </p>
                )}
              </div>
            </section>
          )}
        </>
      )}

      {/* Empty State */}
      {activeDuels.length === 0 && completedDuels.length === 0 && (
        <Card className="text-center p-10">
          <CardContent>
            <div className="w-16 h-16 mx-auto mb-4 bg-surface-100 dark:bg-surface-800 rounded-full flex items-center justify-center">
              <Users className="w-8 h-8 text-surface-400" />
            </div>
            <h2 className="text-xl font-bold text-surface-900 dark:text-surface-100 mb-2">No duels yet</h2>
            <p className="text-surface-500 dark:text-surface-400 mb-6 max-w-md mx-auto">
              Create a duel to challenge a friend! You'll both answer today's prompt and see how in sync you are.
            </p>
            <Button 
              size="lg" 
              onClick={handleCreateDuel} 
              loading={creating}
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Duel
            </Button>
          </CardContent>
        </Card>
      )}

      {/* How it works */}
      <Card>
        <CardContent className="pt-6">
          <h3 className="font-semibold text-surface-900 dark:text-surface-100 mb-4">How Duels Work</h3>
          <div className="space-y-3 text-sm text-surface-600 dark:text-surface-400">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-brand-100 dark:bg-brand-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-brand-600 dark:text-brand-400">1</span>
              </div>
              <span>Create a duel from today's prompt - a unique invite code is generated</span>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-brand-100 dark:bg-brand-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-brand-600 dark:text-brand-400">2</span>
              </div>
              <span>Share the link with a friend - they'll join and answer the same prompt</span>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-brand-100 dark:bg-brand-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-brand-600 dark:text-brand-400">3</span>
              </div>
              <span>Both of you predict the crowd average - highest sync score wins!</span>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-brand-100 dark:bg-brand-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-brand-600 dark:text-brand-400">4</span>
              </div>
              <span>Results show your Overlap % - are you Soulmates or Total Opposites?</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}